/* Copyright 2024 Marimo. All rights reserved. */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { tableFromIPC } from "@uwdata/flechette";
import { batch } from "@/utils/batch-requests";
import { getRuntimeManager } from "@/core/runtime/config";
import { createLoader, type Loader } from "./vega-loader";

export function createBatchedLoader(): Loader {
  const loader = withAuthSupport(createLoader());
  const toKey = (request: unknown) => JSON.stringify(request);
  return {
    load: batch(loader.load.bind(loader) as any, toKey),
    sanitize: batch(loader.sanitize.bind(loader) as any, toKey),
    http: batch(loader.http.bind(loader) as any, toKey),
    file: batch(loader.file.bind(loader), toKey),
  };
}

export function withAuthSupport(loader: Loader): Loader {
  return {
    ...loader,
    async load(uri: string, options?: unknown) {
      // Handle arrow files with auth headers
      if (uri.endsWith(".arrow")) {
        const arrow = await batchedArrowLoader(uri);
        return tableFromIPC(arrow, {
          // useProxy=true makes aggregations like year(data) fail
          useProxy: false,
        }).toArray();
      }

      // Handle other HTTP/HTTPS URLs with auth headers
      if (uri.startsWith("http://") || uri.startsWith("https://")) {
        return await batchedHttpLoader(uri);
      }

      // For non-HTTP URLs (data:, blob:, etc.), use base loader
      return loader.load(uri, options);
    },
    async http(uri: string, options?: unknown) {
      // Handle HTTP/HTTPS URLs with auth headers
      if (uri.startsWith("http://") || uri.startsWith("https://")) {
        return await batchedHttpLoader(uri);
      }

      // For non-HTTP URLs, use base loader
      return loader.http(uri, options);
    },
  };
}

/**
 * Batch requests to the same URL returning the same promise for all calls with the same key.
 * Used for arrow files (returns arrayBuffer).
 */
export const batchedArrowLoader = batch(
  (url: string) => {
    const runtimeManager = getRuntimeManager();
    const headers = runtimeManager.headers('server');
    return fetch(url, { headers }).then((r) => r.arrayBuffer());
  },
  (url: string) => url,
);

/**
 * Batch requests to the same URL returning the same promise for all calls with the same key.
 * Used for CSV, JSON, and other text-based files (returns text).
 */
export const batchedHttpLoader = batch(
  (url: string) => {
    const runtimeManager = getRuntimeManager();
    const headers = runtimeManager.headers('server');
    return fetch(url, { headers }).then((r) => r.text());
  },
  (url: string) => url,
);
